import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useStats } from '@/hooks/useStats'
import { useActivity } from '@/hooks/useActivity'
import { useHealth } from '@/hooks/useHealth'
import { TokenUsageWidget } from '@/components/widgets/TokenUsageWidget'
import { ExecutiveStrip } from '@/components/dashboard/ExecutiveStrip'
import { QuickActions } from '@/components/dashboard/QuickActions'
import { NeedsAttention } from '@/components/dashboard/NeedsAttention'
import { AgentActivitySummary } from '@/components/dashboard/AgentActivitySummary'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { timeAgo } from '@/lib/utils'

function formatMs(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`
  return `${Math.round(ms / 60_000)}m`
}

export function DashboardHome() {
  const stats = useStats()
  const activity = useActivity(5)
  const health = useHealth()
  const [criticalOnly, setCriticalOnly] = useState(false)

  return (
    <div className="space-y-6">
      {/* Quick actions + what needs Mark right now (top of the hierarchy) */}
      <ErrorBoundary label="Quick Actions" compact>
        <QuickActions
          criticalOnly={criticalOnly}
          onToggleCriticalOnly={() => setCriticalOnly((v) => !v)}
        />
      </ErrorBoundary>
      <ErrorBoundary label="Needs Attention" compact>
        <NeedsAttention criticalOnly={criticalOnly} />
      </ErrorBoundary>

      {/* Zeus + Athena executive strip — peers under Mark (DEC-021) */}
      <ErrorBoundary label="Executive Strip" compact>
        <ExecutiveStrip />
      </ErrorBoundary>

      {/* Stat cards */}
      <ErrorBoundary label="Key Stats" compact>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle>DECISIONS TODAY</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.isLoading ? (
              <Skeleton className="h-10 w-24" />
            ) : (
              <p className="font-mono text-[2rem] font-medium text-white tabular-nums">
                {stats.data?.decisionsToday ?? 0}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>PENDING</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.isLoading ? (
              <Skeleton className="h-10 w-24" />
            ) : (
              <p className="font-mono text-[2rem] font-medium text-cmd-error tabular-nums">
                {stats.data?.pendingDecisions ?? 0}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>ACTIVATIONS (24H)</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.isLoading ? (
              <Skeleton className="h-10 w-24" />
            ) : (
              <p className="font-mono text-[2rem] font-medium text-white tabular-nums">
                {stats.data?.activationsLast24h ?? 0}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>AVG RESPONSE</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.isLoading ? (
              <Skeleton className="h-10 w-24" />
            ) : (
              <p className="font-mono text-[2rem] font-medium text-white tabular-nums">
                {stats.data?.avgResponseTimeMs ? formatMs(stats.data.avgResponseTimeMs) : '--'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
      </ErrorBoundary>

      {/* Token Usage + Today's Agent Activity */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <ErrorBoundary label="Token Usage" compact>
            <TokenUsageWidget />
          </ErrorBoundary>
        </div>
        <div className="lg:col-span-2">
          <ErrorBoundary label="Today’s Agent Activity" compact>
            <AgentActivitySummary />
          </ErrorBoundary>
        </div>
      </div>

      {/* Recent Activity + System Health */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ErrorBoundary label="Recent Activity" compact>
        <Card>
          <CardHeader>
            <CardTitle>RECENT ACTIVITY</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            {activity.isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <Skeleton className="h-6 w-6 shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3 w-3/4" />
                    <Skeleton className="h-2.5 w-1/2" />
                  </div>
                </div>
              ))
            ) : activity.data?.data?.length ? (
              activity.data.data.map((entry, i) => (
                <div
                  key={entry.id}
                  className={`flex items-start gap-3 p-3 ${i % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'}`}
                >
                  <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center text-xs font-mono">
                    {entry.metadata?.outcome === 'failed' ? (
                      <span className="text-cmd-error">!</span>
                    ) : (
                      <span className="text-cmd-outline">~</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white/90 truncate">{entry.summary}</p>
                    <p className="font-label text-[0.65rem] text-cmd-outline mt-0.5">
                      <span className="text-primary">{entry.agentName}</span>
                      <span className="mx-1.5">/</span>
                      <span className="font-mono">{timeAgo(entry.createdAt)}</span>
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-cmd-outline p-3 font-label">NO SIGNAL</p>
            )}
          </CardContent>
        </Card>
        </ErrorBoundary>

        <ErrorBoundary label="System Health" compact>
        <Card>
          <CardHeader>
            <CardTitle>SYSTEM HEALTH</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            {health.isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between p-3">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-16" />
                </div>
              ))
            ) : health.data ? (
              <>
                <div className="flex items-center justify-between p-3 bg-cmd-low">
                  <span className="font-label text-xs text-cmd-outline uppercase">Overall</span>
                  <Badge variant={health.data.status === 'healthy' ? 'success' : 'destructive'}>
                    {health.data.status === 'healthy' ? 'ONLINE' : 'FAULT DETECTED'}
                  </Badge>
                </div>
                {health.data.services.map((svc, i) => (
                  <div
                    key={svc.name}
                    className={`flex items-center justify-between p-3 ${i % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-0.5 h-4 ${
                        svc.status === 'healthy' ? 'bg-primary' :
                        svc.status === 'degraded' ? 'bg-[#ffd080]' : 'bg-cmd-error'
                      }`} />
                      <span className="font-label text-xs text-white/80 uppercase">{svc.name}</span>
                    </div>
                    <span className="font-mono text-[0.65rem] text-cmd-outline">{svc.detail}</span>
                  </div>
                ))}
              </>
            ) : (
              <p className="text-sm text-cmd-outline p-3 font-label">NO SIGNAL</p>
            )}
          </CardContent>
        </Card>
        </ErrorBoundary>
      </div>
    </div>
  )
}
